import {
  authReducer,
  credentialsReceived,
  signedOut,
  type AuthState,
} from "../features/auth/authSlice";
import { portalCredentialStorage } from "../features/auth/portalCredentialStorage";
import { configureStore, createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { gboApi } from "./gboApi";
import { checkoutCleared, checkoutReducer } from "../features/checkout/checkoutSlice";
import { portalIdentity } from "../features/auth/portalIdentity";
import { getSelectedLocalTenant, setSelectedLocalTenant } from "../features/tenant/localTenants";
import { localTenantChanged, tenantReducer } from "../features/tenant/tenantSlice";
import type { TenantState } from "../features/tenant/tenantSlice";
import { readEmbeddedSiteConfig } from "../lib/siteConfigBootstrap";

type AppPreloadedState = {
  tenant?: TenantState;
  auth?: AuthState;
};

type CreateAppStoreOptions = {
  preloadedState?: AppPreloadedState;
  persistLocalTenant?: boolean;
};

const reducer = {
  auth: authReducer,
  checkout: checkoutReducer,
  tenant: tenantReducer,
  [gboApi.reducerPath]: gboApi.reducer,
};

export function createDefaultTenantState(): TenantState {
  const embeddedSiteConfig = typeof document === "undefined" ? null : readEmbeddedSiteConfig();

  return {
    localTenantHost: getSelectedLocalTenant(),
    currentSiteConfig: embeddedSiteConfig,
  };
}

export function createAppStore({
  preloadedState,
  persistLocalTenant = true,
}: CreateAppStoreOptions = {}) {
  const listenerMiddleware = createListenerMiddleware();

  listenerMiddleware.startListening({
    actionCreator: credentialsReceived,
    effect: ({ payload }, api) => {
      const previous = (api.getOriginalState() as { auth: AuthState }).auth.credentials;
      portalCredentialStorage.save(payload);
      if (portalIdentity.scope(previous) !== portalIdentity.scope(payload)) {
        api.dispatch(checkoutCleared());
        api.dispatch(gboApi.util.invalidateTags(["Invoice", "Account"]));
      }
    },
  });
  listenerMiddleware.startListening({
    matcher: isAnyOf(signedOut, localTenantChanged),
    effect: (_action, api) => {
      portalCredentialStorage.clear();
      api.dispatch(checkoutCleared());
    },
  });

  if (persistLocalTenant) {
    listenerMiddleware.startListening({
      actionCreator: localTenantChanged,
      effect: (action, api) => {
        setSelectedLocalTenant(action.payload);
        api.dispatch(gboApi.util.resetApiState());
      },
    });
  }

  return configureStore({
    reducer,
    preloadedState: {
      ...preloadedState,
      auth: preloadedState?.auth ?? portalCredentialStorage.read(),
      tenant: preloadedState?.tenant ?? createDefaultTenantState(),
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().prepend(listenerMiddleware.middleware).concat(gboApi.middleware),
    devTools: import.meta.env.DEV,
  });
}

export const store = createAppStore();

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore["dispatch"];
export type RootState = ReturnType<AppStore["getState"]>;
